/**
 * Decision 0154: one-line insights for a lead (or an item's agent) that the record already knows and a turn would not
 * notice on its own. Insights, not rules: each line says what is true and what usually helps; the agent decides.
 * - a new person writes for the first time (`newcomerLine`, onboarding 10-23 #8);
 * - an item has sat in review with no review running (`idleReviews`, review 10-23 #5);
 * - an item was closed (merged) over a failing validator verdict, and its major findings have neither a scheduled item
 *   nor a line in a brief (`mergedOverReview`, review 10-23 #6).
 * Decision 0156 (onboarding-week review B1–B6):
 * - the newcomer line also says who the agent is, how to work with it and to write plainly for non-technical people;
 * - a turn a person started keeps its reply about them (`replyFocusLine`);
 * - a people page's `## Decides` changed: re-check who answers open items and cards (`decidesChangedLine`);
 * - work runs waiting long for a runner, with Minga's reason (`longWaits`);
 * - items with heavy checks and no worker profile (`heavyCheckItems`).
 * Decision 0158 (onboarding-week "this week"):
 * - an overview that is still the template, no active goal, members whose pages have no Decides (`projectShapeLines`);
 * - a lead's first conversation in a project (`FIRST_CONVERSATION_LINE`);
 * - the asker's Decides and how they like to hear about work, next to who they are (`askerPageLines`).
 * Pure.
 */
import { isSeededTemplate } from "@minga/protocol"
import { refsIn } from "./knowledge/refs-text"
import { recipientOf } from "./multiplayer"
import { pageOf } from "./pages"
import { agentAbove } from "./people"
import { isOpenItem, isOpenProposal, isRunEnded } from "./state"
import type { AgentId, Ids, Item, ProjectState, Run, WakeReason } from "./types"
import { waitText } from "./unmet-words"
import { personCaused } from "./wakes"

const HOUR = 3_600_000
const DAY = 24 * HOUR

const age = (ms: number): string => {
  const h = Math.max(1, Math.floor(ms / HOUR))
  return h < 48 ? `${h} h` : `${Math.floor(ms / DAY)} days`
}

// #region newcomer
/** Did this person write in the batch themselves (a chat message, an @mention, a plain note)? */
export const writesInBatch = (reasons: ReadonlyArray<WakeReason>, userId: Ids["UserId"]): boolean =>
  reasons.some(
    (r) =>
      ((r._tag === "user_message" || r._tag === "human_mention") && r.from === userId) ||
      (r._tag === "changed" && r.by._tag === "user" && r.by.userId === userId && r.note !== undefined),
  )

/**
 * Onboarding 10-23 #8: the line for a turn that answers a person writing to the agent for the first time in this
 * project. `metBefore`: a turn was asked by them before (the Project's run history). Only in a project with more than one
 * person, never for the agent's keeper (they set it up), and only when they wrote in this batch themselves. The agent
 * decides what to do with it; the line names what usually helps.
 */
export const newcomerLine = (
  s: ProjectState,
  opts: {
    readonly userId: Ids["UserId"] | null | undefined
    readonly keeper: Ids["UserId"] | null | undefined
    readonly metBefore: boolean
    readonly reasons: ReadonlyArray<WakeReason>
  },
): string | null => {
  const { userId } = opts
  if (!userId || opts.metBefore || userId === opts.keeper || s.members.length <= 1) return null
  const m = s.members.find((x) => x.userId === userId)
  if (!m || !writesInBatch(opts.reasons, userId)) return null
  const name = m.displayName.trim() || `@${m.handle}`
  // decision 0156 (B1): who the agent is and how to work with it, and their register (onboarding-week review G1, G3)
  return `→ ${name} (@${m.handle}) is writing to you for the first time in this project. Meet them where they are. Say in two or three sentences who you are and how they work with you: you plan the work and hand it to workers; their decisions reach them as cards with buttons (in Needs you); a note they leave on an item reaches you; nothing in their area starts without their Start or their go. Then what the project is, where it stands and what they can do now, in their terms, not the plan's mechanics. Write for them: when their people page, its Decides or the way they write says they are not technical, use plain words, without refs, PR, run or validator numbers unless they ask. Learn what they decide, and when you know, propose their people page (\`workspace/people/${m.handle}.md\`) with a \`## Decides\` section, unless it already has one.`
}
// #endregion

// #region a reply to a person
/**
 * Decision 0156 (B2, onboarding-week review G2): a turn a person started that also carries other events (run results,
 * reviews, blockers, alerts). The line says whom the reply is for and where the rest belongs; the agent decides.
 */
export const replyFocusLine = (
  s: ProjectState,
  reasons: ReadonlyArray<WakeReason>,
  askedBy: Ids["UserId"] | null | undefined,
): string | null => {
  if (!askedBy) return null
  const theirs = reasons.filter(personCaused)
  const other = reasons.length - theirs.length
  if (theirs.length === 0 || other === 0) return null
  const m = s.members.find((x) => x.userId === askedBy)
  const name = m ? m.displayName.trim() || `@${m.handle}` : "the person"
  return `→ This turn answers ${name}. ${other} other event${other === 1 ? "" : "s"} below (run results, reviews, alerts) ${other === 1 ? "is" : "are"} team operations: handle ${other === 1 ? "it" : "them"} in this turn, but report ${other === 1 ? "it" : "them"} where ${other === 1 ? "it belongs" : "they belong"} (a comment on the item, status.md, the next brief), not in your reply to ${name}, unless they asked for everything or it changes what they asked about.`
}
// #endregion

// #region decides changed
/** The text under the first `## ` heading matching `heading` (to the next `##`), trimmed; null when there is none. */
export const pageSection = (text: string | null | undefined, heading: RegExp): string | null => {
  if (!text) return null
  const lines = text.split("\n")
  const at = lines.findIndex((l) => {
    const m = /^##\s+(.+?)\s*$/.exec(l.trim())
    return m !== null && heading.test(m[1] ?? "")
  })
  if (at < 0) return null
  const rest = lines.slice(at + 1)
  const end = rest.findIndex((l) => /^##\s/.test(l.trim()))
  return (end < 0 ? rest : rest.slice(0, end)).join("\n").trim()
}

/** The text under a page's `## Decides` heading (to the next `##`), trimmed; null when the page has none. */
export const decidesSection = (text: string | null | undefined): string | null =>
  pageSection(text, /^decides$/i)

/**
 * Decision 0158: how a person likes to hear about work, from their people page: a `## How I like to hear about work`
 * section (or any `## How … hear/write/talk …` heading). Null when the page has none.
 */
export const registerSection = (text: string | null | undefined): string | null =>
  pageSection(text, /^how\b.*\b(hear|write|talk|updates?)\b/i)

/** Decision 0156 (B3): a people page whose `## Decides` changed since the agent's last turn. */
export interface DecidesChange {
  readonly handle: string
  readonly version: number
  /** Who saved that version ("Jannik"). */
  readonly by: string
  /** The page had no `## Decides` before (a member's first). */
  readonly first: boolean
}

/** Did the Decides section change between two versions of a people page (before null: a new page)? */
export const decidesChanged = (before: string | null, after: string | null): boolean | "first" => {
  const a = decidesSection(after)
  if (a === null) return false
  const b = decidesSection(before)
  if (b === null) return "first"
  return a.replace(/\s+/g, " ") !== b.replace(/\s+/g, " ")
}

/** At most this many open records the Decides line names (the rest: read_graph). */
const MAX_DECIDES_REFS = 8

/**
 * Decision 0156 (B3, the REP-140/REP-43 slip): the line for people pages whose Decides changed since the agent's last
 * turn, naming the open items assigned to people and the open cards waiting on people, so the agent re-checks who
 * answers each under the new split. Insight only: moving any of them is the agent's call (a person's item is a card).
 */
export const decidesChangedLine = (s: ProjectState, changes: ReadonlyArray<DecidesChange>): string | null => {
  if (changes.length === 0) return null
  const name = (userId: string) =>
    s.members.find((m) => m.userId === userId)?.displayName.trim() || "a person"
  const items = s.items
    .filter((t) => isOpenItem(t) && t.assignee?._tag === "user")
    .map((t) => `${t.ref} (${name(t.assignee?._tag === "user" ? t.assignee.userId : "")})`)
  const cards = s.proposals
    .filter((p) => isOpenProposal(p) && p.approver._tag === "human")
    .map((p) => `${p.ref} (${name(recipientOf(s, p))})`)
  const open = [...items, ...cards]
  const who = changes
    .map(
      (c) =>
        `@${c.handle}'s ${c.first ? "page now has a Decides section" : "Decides changed"} (v${c.version}, by ${c.by})`,
    )
    .join("; ")
  const list =
    open.length === 0
      ? "Nothing open waits on a person now."
      : `Open now: ${open.slice(0, MAX_DECIDES_REFS).join(", ")}${open.length > MAX_DECIDES_REFS ? ` and ${open.length - MAX_DECIDES_REFS} more (read_graph)` : ""}.`
  return `→ ${who}. Re-check who answers each open item assigned to a person and each open card under the new split: an item or card that is now someone else's decision goes to them (a person's item changes on a card to its assignee; a card you opened you can withdraw and re-file with \`to\`). ${list}`
}
// #endregion

// #region runs waiting for a runner
/** Decision 0156 (B4): a work run waiting for a runner longer than this reaches the lead's turn as a line. */
export const LONG_WAIT_MS = HOUR

export interface LongWait {
  readonly run: Run
  readonly item: Item
  readonly since: number
}

/** Worker and validator runs that have waited for a runner (queued or waiting) longer than `LONG_WAIT_MS`. */
export const longWaits = (s: ProjectState, now: number, after = LONG_WAIT_MS): ReadonlyArray<LongWait> => {
  const out: LongWait[] = []
  for (const r of s.runs) {
    // partial runs (hand-built states, older fixtures) carry no state: nothing to say
    if (r.kind === "turn" || !r.state || isRunEnded(r)) continue
    if (r.state._tag !== "requested" && r.state._tag !== "waiting") continue
    const item = r.itemId ? s.items.find((t) => t.id === r.itemId) : undefined
    if (!item) continue
    const since = r.state._tag === "waiting" ? Math.min(r.state.since, r.requestedAt) : r.requestedAt
    if (now - since < after) continue
    out.push({ run: r, item, since })
  }
  return out.sort((a, b) => a.since - b.since)
}

/** One long wait in words, with Minga's reason ("W-129 for DIC-123 has waited 14 h for a runner: …"). */
export const longWaitLine = (w: LongWait, now: number, name?: (runnerId: string) => string): string => {
  const who = w.run.worker?.ref ?? w.run.ref
  const unmet = w.run.state._tag === "waiting" ? w.run.state.unmet : []
  return `${who} for ${w.item.ref} has waited ${age(now - w.since)} for a runner: ${w.run.state._tag === "waiting" ? waitText(unmet, name) : "queued, no runner has answered yet"}`
}
// #endregion

// #region heavy checks
/**
 * Words in a command check that usually mean a long or heavy run (a browser suite, a full test run, restart or runtime
 * proofs, a build). Decision 0165: placement reads the same words (`heavyCheckOf`), so the rule stays one regex anyone
 * can read: a check whose name or description says one of them is heavy.
 */
const HEAVY =
  /\b(e2e|end-to-end|playwright|cypress|browser|full (test )?suite|test:all|integration|restart|runtime (test )?suite|xcodebuild|docker build)\b/i

/**
 * Decision 0165: the first command check of an item whose name or description looks heavy (`HEAVY`), or null. Placement
 * prefers a bigger Platform sandbox for such an item's runs when nothing routes them to a runner or profile.
 */
export const heavyCheckOf = (t: Pick<Item, "checks">): string | null => {
  const c = (t.checks ?? []).find((x) => x._tag === "command" && HEAVY.test(`${x.name} ${x.description}`))
  return c?._tag === "command" ? c.name : null
}

/**
 * Decision 0165: the heavy check a worker or validator run carries to the hub, or null. Null when the run's frozen
 * placement names a runner or a profile anywhere (an item route, a project routing rule, a pin, preferred or required):
 * explicit routing always wins, and Minga adds nothing to it.
 */
export const runHeavyCheck = (
  s: ProjectState,
  run: Pick<Run, "kind" | "itemId" | "placement">,
): string | null => {
  if ((run.kind !== "worker" && run.kind !== "validator") || !run.itemId) return null
  const p = run.placement
  if (p.requires.runner !== undefined || p.requires.profile !== undefined) return null
  if (p.prefers.some((x) => x.match.runner !== undefined || x.match.profile !== undefined)) return null
  const item = s.items.find((t) => t.id === run.itemId)
  return item ? heavyCheckOf(item) : null
}

/** Decision 0156 (B6): an item not started yet whose checks look heavy and that names no worker profile. */
export interface HeavyCheckItem {
  readonly item: Item
  /** The heavy check's name ("pnpm test:e2e"). */
  readonly check: string
}

/**
 * Worker leaves in todo (not started: Ready, Now or Planning) whose command checks look heavy and whose routing names
 * no worker profile. The lead decides whether a bigger machine fits: an insight, not a rule.
 */
export const heavyCheckItems = (s: ProjectState): ReadonlyArray<HeavyCheckItem> => {
  const out: HeavyCheckItem[] = []
  for (const t of s.items) {
    if (t.status !== "todo" || t.assignee?._tag !== "worker" || t.routing?.worker?.profileId) continue
    const check = heavyCheckOf(t)
    if (check !== null) out.push({ item: t, check })
  }
  return out
}
// #endregion

// #region idle reviews
/** An item in review is idle after this long with no review running (review 10-23 #5: REP-99 sat 17 h). */
export const IDLE_REVIEW_AFTER_MS = HOUR

export interface IdleReview {
  readonly item: Item
  readonly stage: "agent" | "validator"
  /** Since when it waits (the item's last change: entering review, or its last review). */
  readonly since: number
  /** The agent that answers for it: its owner agent, else the agent above it. */
  readonly agentId: AgentId | null
}

/**
 * Items in review for over an hour with no review running: at an agent stage (its agent's `review` is due) or at a
 * validator stage with no live validator. A human stage waits on a person's card (the card's own loose end covers it);
 * a blocked or paused item is not in review.
 */
export const idleReviews = (s: ProjectState, now: number): ReadonlyArray<IdleReview> => {
  const out: IdleReview[] = []
  for (const t of s.items) {
    if (t.status !== "in_review" || t.paused) continue
    // partial items (older test states, hand-built states) carry no review: nothing to say
    const stage = t.review?.stages[t.review.stage]
    if (stage !== "agent" && stage !== "validator") continue
    if (now - t.updatedAt < IDLE_REVIEW_AFTER_MS) continue
    if (
      stage === "validator" &&
      s.runs.some((r) => r.kind === "validator" && r.itemId === t.id && !isRunEnded(r))
    )
      continue
    const agentId = t.owner?._tag === "agent" ? t.owner.agentId : (agentAbove(s, t)?.id ?? null)
    out.push({ item: t, stage, since: t.updatedAt, agentId })
  }
  return out.sort((a, b) => a.since - b.since)
}

/** One line for an idle review (the turn file's truth lines and the daily check's loose end). */
export const idleReviewLine = (r: IdleReview, now: number): string =>
  r.stage === "agent"
    ? `${r.item.ref} has waited in review for ${age(now - r.since)} at its agent stage with no review running: \`review\` it (approve or request changes), or say why it waits`
    : `${r.item.ref} has waited in review for ${age(now - r.since)} at its validator stage with no validator running: \`retry_item\` re-runs the validator, or revise its checks`
// #endregion

// #region merged over review
export interface MergedOverReview {
  readonly item: Item
  /** The validator run whose verdict failed last. */
  readonly validator: Run
  /** Its findings marked major or blocker. */
  readonly majors: ReadonlyArray<string>
  /** Open items that name it and have a due date and a planned status (todo, Now, in review, blocked). */
  readonly scheduledIn: ReadonlyArray<Item>
  /** A brief sent after it closed names it. */
  readonly briefed: boolean
}

const SCHEDULED: ReadonlySet<string> = new Set(["todo", "in_progress", "in_review", "blocked"])

/**
 * Review 10-23 #6: items closed done since `since` whose latest validator verdict was a fail with major (or blocker)
 * findings: a lead merged over a failing review. `runsOf` adds an item's runs from the run history (compaction keeps only
 * each item's latest run in state). Each names the open items that schedule a follow-up (they mention the item's ref and
 * have a due date, not in Planning) and whether a brief sent after the close names it.
 */
export const mergedOverReview = (
  s: ProjectState,
  since: number,
  runsOf?: (itemId: Ids["ItemId"]) => ReadonlyArray<Run>,
): ReadonlyArray<MergedOverReview> => {
  const out: MergedOverReview[] = []
  const briefs = Object.values(s.briefsTo ?? {}).flatMap((byPerson) => Object.values(byPerson))
  const anyBriefs = Object.values(s.briefs ?? {})
  for (const t of s.items) {
    if (t.status !== "done" || t.closedAt === null || t.closedAt < since) continue
    const runs = new Map<string, Run>()
    for (const r of [...s.runs, ...(runsOf?.(t.id) ?? [])])
      if (r.kind === "validator" && r.itemId === t.id && r.verdict) runs.set(r.id, r)
    const last = [...runs.values()].sort((a, b) => a.requestedAt - b.requestedAt).at(-1)
    if (last?.verdict?._tag !== "fail") continue
    const majors = last.verdict.findings
      .filter((f) => f.severity === "major" || f.severity === "blocker")
      .map((f) => f.text.trim())
    if (majors.length === 0) continue
    const closedAt = t.closedAt
    const scheduledIn = s.items.filter(
      (x) =>
        x.id !== t.id &&
        isOpenItem(x) &&
        SCHEDULED.has(x.status) &&
        x.dueAt !== null &&
        refsIn(`${x.title}\n${x.why}\n${x.body}`).includes(t.ref),
    )
    const briefed = [...briefs, ...anyBriefs].some((b) => b.at >= closedAt && refsIn(b.body).includes(t.ref))
    out.push({ item: t, validator: last, majors, scheduledIn, briefed })
  }
  return out.sort((a, b) => (a.item.closedAt ?? 0) - (b.item.closedAt ?? 0))
}

/** "REP-72 (V-78: 2 major)" */
export const mergedOverShort = (m: MergedOverReview): string =>
  `${m.item.ref} (${m.validator.ref}: ${m.majors.length} major)`

/**
 * The lead's truth line for one merged-over item that neither a scheduled item nor a brief covers yet (review 10-23 #6:
 * two majors sat in backlog items nothing scheduled).
 */
export const mergedOverLine = (m: MergedOverReview): string => {
  const first = m.majors[0] ?? ""
  const quote = first.length > 140 ? `${first.slice(0, 139)}…` : first
  return `→ ${m.item.ref} was closed over ${m.validator.ref}'s failing verdict (${m.majors.length} major: "${quote}"${m.majors.length > 1 ? " …" : ""}). A major you merge over needs an owner and a date: plan the fix as an item with a \`dueAt\`, in Ready or Now, that names ${m.item.ref}, or name it in the next brief with who fixes it and by when. A backlog item alone is not enough.`
}

/**
 * The merged-over items a lead still has to give an owner and a date: closed in the last 7 days, not scheduled by an
 * item and not named in a brief since.
 */
export const mergedOverOpen = (
  s: ProjectState,
  now: number,
  runsOf?: (itemId: Ids["ItemId"]) => ReadonlyArray<Run>,
): ReadonlyArray<MergedOverReview> =>
  mergedOverReview(s, now - 7 * DAY, runsOf).filter((m) => m.scheduledIn.length === 0 && !m.briefed)
// #endregion

// #region project shape (decision 0158)
/** Decision 0158: what a people page says about its person, for the turn file. */
export interface PersonPage {
  /** Their `## Decides` section; null when the page has none. */
  readonly decides: string | null
  /** How they like to hear about work (`## How I like to hear about work`); null when the page says nothing. */
  readonly register: string | null
}

/** The overview is still the pages job's seeded template (nobody wrote what the project is). */
export const overviewIsTemplate = (s: ProjectState): boolean => {
  const page = pageOf(s, "overview")
  return page !== undefined && isSeededTemplate(page)
}

/**
 * Decision 0158 (lead-experience G5, 10-23 D): truth lines about a project's empty places that a lead would not notice
 * on its own: the overview is still the template, no goal is active, people in a shared project whose pages say
 * nothing about what they decide. Insights, not rules: each says what is true and what usually helps.
 */
export const projectShapeLines = (
  s: ProjectState,
  opts: {
    /** Handles of working members whose people page has no `## Decides` (or no page); absent: unknown, no line. */
    readonly withoutDecides?: ReadonlyArray<string> | undefined
  } = {},
): string[] => {
  const out: string[] = []
  if (overviewIsTemplate(s))
    out.push(
      "→ project/overview.md is still the seeded template: nothing says yet what the project is, for whom and their loop, its scope and non-goals. The person's words for these usually come up in conversation; draft it with them and `propose_page` it when you have enough. A plan reads better after it.",
    )
  if (!s.goals.some((g) => g.status === "active"))
    out.push(
      "→ No goal yet: nothing says how the people here will know this worked. When you know enough, propose one with a signal they would watch (a KPI, or a result by a date).",
    )
  const missing = opts.withoutDecides ?? []
  if (s.members.length > 1 && missing.length > 0)
    out.push(
      `→ No \`## Decides\` on the people page${missing.length === 1 ? "" : "s"} of ${missing.map((h) => `@${h}`).join(", ")}: cards go to whoever asked until a page says who decides what. When a conversation shows it, propose their page (\`workspace/people/<handle>.md\`) with a \`## Decides\` section in their words.`,
    )
  return out
}

/**
 * Decision 0158 (onboarding 10-23 D): the line for a lead's first conversation in a project: a person started the turn
 * and the lead never had a turn here before. One line; the agent decides.
 */
export const FIRST_CONVERSATION_LINE =
  "→ This is your first conversation in this project. What usually helps: say in two or three sentences who you are and how you work; draft the overview with them in their words (`propose_page`) before a plan; propose a goal with a signal they would watch; propose the first release's workstreams; and say that work starts only on their Start or their go."

const clipOne = (t: string, n: number): string => {
  const one = t.replace(/\s+/g, " ").trim()
  return one.length > n ? `${one.slice(0, n - 1)}…` : one
}

/**
 * Decision 0158 (lead-experience #9): what the asker decides and how they like to hear about work, quoted from their
 * people page (clipped), next to who they are in the turn file. `undefined`: unknown (pages unavailable), no line;
 * `null`: no people page.
 */
export const askerPageLines = (name: string, page: PersonPage | null | undefined): string[] => {
  if (page === undefined) return []
  if (page === null || page.decides === null)
    return [
      `${name}'s people page says nothing yet about what they decide${page?.register ? ` (how they like to hear about work: ${JSON.stringify(clipOne(page.register, 240))})` : ""}: judge from what they write, and propose their \`## Decides\` once you know.`,
    ]
  return [
    `${name} decides (their people page): ${JSON.stringify(clipOne(page.decides, 400))}.${
      page.register !== null
        ? ` How they like to hear about work: ${JSON.stringify(clipOne(page.register, 240))}.`
        : ""
    } Answer them at that depth and in that register.`,
  ]
}
// #endregion
